import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  TextField,
  Divider,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Stack,
  Paper,
} from '@mui/material';
import {
  Edit,
  Person,
  Email,
  Phone,
  Save,
  Cancel,
  CloudUpload,
  Lock,
  Shield,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { userAPI } from '../services/api';

interface UserData {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  role: 'rider' | 'driver';
}

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (user) {
      setUserData({
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        avatar: user.avatar,
        role: user.role,
      });
      setEditForm({
        name: user.name,
        email: user.email,
        phone: user.phone,
      });
    }
  }, [user]);

  const handleEditToggle = () => {
    if (isEditing) {
      if (user) {
        setEditForm({
          name: user.name,
          email: user.email,
          phone: user.phone,
        });
        setAvatarPreview(null);
        setSelectedAvatar(null);
      }
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setIsLoading(true);

      if (selectedAvatar) {
        const formData = new FormData();
        formData.append('avatar', selectedAvatar);

        const avatarResponse = await userAPI.updateAvatar(formData);
        if (avatarResponse.success && avatarResponse.data) {
          setAvatarPreview(avatarResponse.data.avatar);
        }
      }

      const response = await userAPI.updateProfile({
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
      });

      if (response.success && response.data) {
        updateUser(response.data);
        showSnackbar('Profil məlumatları yeniləndi', 'success');
        setIsEditing(false);
      } else {
        showSnackbar(response.message || 'Profil yenilənə bilmədi', 'error');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      showSnackbar('Profil yenilənə bilmədi', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showSnackbar('Yeni şifrələr uyğun gəlmir', 'error');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showSnackbar('Şifrə ən azı 6 simvol olmalıdır', 'error');
      return;
    }

    try {
      setIsLoading(true);
      const response = await userAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      if (response.success) {
        showSnackbar('Şifrə uğurla dəyişdirildi', 'success');
        setIsPasswordDialogOpen(false);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        showSnackbar(response.message || 'Şifrə dəyişdirilə bilmədi', 'error');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      showSnackbar('Şifrə dəyişdirilə bilmədi', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!userData) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography>Məlumatlar yüklənir...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
      {}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #FF006E 0%, #2196F3 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          Profil
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          Şəxsi məlumatlarınızı idarə edin
        </Typography>
      </Box>

      {}
      <Card 
        sx={{ 
          mb: { xs: 3, sm: 4 },
          borderRadius: { xs: 3, sm: 4 },
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Şəxsi məlumatlar
            </Typography>
            <IconButton 
              onClick={handleEditToggle} 
              color="primary"
              sx={{
                bgcolor: isEditing ? 'error.main' : 'primary.main',
                color: 'white',
                minWidth: { xs: 48, sm: 56 },
                minHeight: { xs: 48, sm: 56 },
                padding: { xs: 1.5, sm: 2 },
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  bgcolor: isEditing ? 'error.dark' : 'primary.dark',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                  transition: 'transform 0.1s ease-out',
                },
              }}
            >
              {isEditing ? <Cancel /> : <Edit />}
            </IconButton>
          </Box>

          {}
          <Box display="flex" justifyContent="center" mb={3}>
            <Box position="relative">
              <Avatar
                src={avatarPreview || userData.avatar}
                alt={userData.name}
                sx={{ 
                  width: { xs: 100, sm: 120 }, 
                  height: { xs: 100, sm: 120 },
                  border: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: '0 8px 24px rgba(255, 0, 110, 0.2)',
                }}
              />
              {isEditing && (
                <IconButton
                  color="primary"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'white',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                  component="label"
                >
                  <CloudUpload />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleAvatarChange}
                  />
                </IconButton>
              )}
            </Box>
          </Box>

          {}
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Ad"
              name="name"
              value={isEditing ? editForm.name : userData.name}
              onChange={handleInputChange}
              disabled={!isEditing}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color={isEditing ? 'primary' : 'action'} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="E-poçt"
              name="email"
              type="email"
              value={isEditing ? editForm.email : userData.email}
              onChange={handleInputChange}
              disabled={!isEditing}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color={isEditing ? 'primary' : 'action'} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Telefon"
              name="phone"
              value={isEditing ? editForm.phone : userData.phone}
              onChange={handleInputChange}
              disabled={!isEditing}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color={isEditing ? 'primary' : 'action'} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {isEditing && (
            <Box mt={3} display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleSaveProfile}
                disabled={isLoading}
                size="large"
                sx={{ 
                  px: 4,
                  py: { xs: 2, sm: 1.5 },
                  minHeight: { xs: 56, sm: 48 },
                  borderRadius: 2,
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  fontWeight: 600,
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'transform 0.1s ease-out',
                  },
                }}
              >
                {isLoading ? 'Yenilənir...' : 'Yadda saxla'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {}
      <Card
        sx={{
          borderRadius: { xs: 3, sm: 4 },
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Hesab tənzimləmələri
          </Typography>

          <Stack spacing={2}>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'grey.100',
                  transform: 'translateX(4px)',
                },
              }}
              onClick={() => setIsPasswordDialogOpen(true)}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}
                  >
                    <Lock sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Şifrəni dəyişdir
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hesabınızın təhlükəsizliyini artırın
                    </Typography>
                  </Box>
                </Box>
                <IconButton>
                  <Edit />
                </IconButton>
              </Box>
            </Paper>

            <Divider />

            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box display="flex" alignItems="center" mb={1}>
                <Shield sx={{ color: 'primary.main', mr: 1.5, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Hesab növü
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
                Sərnişin
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {}
      <Dialog
        open={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Şifrəni dəyişdir
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Cari şifrə"
              name="currentPassword"
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              fullWidth
              label="Yeni şifrə"
              name="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <TextField
              fullWidth
              label="Yeni şifrəni təsdiqlə"
              name="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
          <Button 
            onClick={() => setIsPasswordDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              minHeight: { xs: 56, sm: 48 },
              py: { xs: 2, sm: 1.5 },
              px: { xs: 3, sm: 2 },
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            Ləğv et
          </Button>
          <Button
            onClick={handleChangePassword}
            disabled={isLoading}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: { xs: 3, sm: 2 },
              py: { xs: 2, sm: 1.5 },
              minHeight: { xs: 56, sm: 48 },
              fontWeight: 600,
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            {isLoading ? 'Dəyişdirilir...' : 'Dəyişdir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;
